import { useEffect, useState } from "react";
import { Store, Star, Phone, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ImageUpload } from "@/components/shared";
import { UploadFolder } from "@/services/filesService";
import { resolveUploadUrl } from "@/services/filesService";
import { useToast } from "@/hooks/use-toast";
import { useMyStore, useStoreId, useUpdateStoreMutation } from "@/hooks/queries";
import type { StoreResponseDto, StoreUpdateDto } from "@/types/store.dto";
import { getApiErrorMessage } from "@/services/httpClient";

function formatTimeForInput(time?: string): string {
  if (!time) return "09:00";
  return time.slice(0, 5);
}

function storeToForm(store: StoreResponseDto) {
  return {
    storeName: store.storeName,
    description: store.description ?? "",
    phone: store.phone,
    email: store.email ?? "",
    fullAddress: store.fullAddress,
    city: store.city,
    state: store.state,
    pincode: store.pincode,
    deliveryRadius: String(store.deliveryRadius),
    minOrderAmount: String(store.minOrderAmount),
    deliveryCharge: String(store.deliveryCharge),
    freeDeliveryAbove: String(store.freeDeliveryAbove),
    hasSelfDelivery: store.hasSelfDelivery,
    openingTime: formatTimeForInput(store.openingTime),
    closingTime: formatTimeForInput(store.closingTime),
    logoUrl: store.logoUrl ?? "",
    bannerUrl: store.bannerUrl ?? "",
  };
}

function formToUpdateDto(form: ReturnType<typeof storeToForm>, store: StoreResponseDto): StoreUpdateDto {
  return {
    storeName: form.storeName,
    description: form.description || undefined,
    phone: form.phone,
    email: form.email || undefined,
    latitude: store.latitude,
    longitude: store.longitude,
    fullAddress: form.fullAddress,
    city: form.city,
    state: form.state,
    pincode: form.pincode,
    hasSelfDelivery: form.hasSelfDelivery,
    deliveryRadius: Number(form.deliveryRadius) || 0,
    minOrderAmount: Number(form.minOrderAmount) || 0,
    deliveryCharge: Number(form.deliveryCharge) || 0,
    freeDeliveryAbove: Number(form.freeDeliveryAbove) || 0,
    openingTime: form.openingTime,
    closingTime: form.closingTime,
    logoUrl: form.logoUrl || undefined,
    bannerUrl: form.bannerUrl || undefined,
  };
}

const StoreProfilePage = () => {
  const { toast } = useToast();
  const { storeId, isLoading: isLoadingStoreId } = useStoreId();
  const { data: store, isLoading } = useMyStore(Boolean(storeId));
  const updateStore = useUpdateStoreMutation(storeId ?? 0);
  const [form, setForm] = useState<ReturnType<typeof storeToForm> | null>(null);

  useEffect(() => {
    if (store) setForm(storeToForm(store));
  }, [store]);

  const handleSave = async () => {
    if (!store || !form || !storeId) return;

    try {
      await updateStore.mutateAsync(formToUpdateDto(form, store));
      toast({ title: "Store profile updated successfully" });
    } catch (error) {
      toast({
        title: "Could not update store",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const updateField = <K extends keyof ReturnType<typeof storeToForm>>(key: K, value: ReturnType<typeof storeToForm>[K]) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  if (isLoadingStoreId || isLoading || !form || !store) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 rounded-xl" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight lg:text-3xl">Store Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your store details and operating settings.</p>
      </div>

      <Card className="bg-gradient-primary text-primary-foreground">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-foreground/20 backdrop-blur overflow-hidden">
                {form.logoUrl ? (
                  <img src={resolveUploadUrl(form.logoUrl)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Store className="h-8 w-8" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold">{store.storeName}</h2>
                <p className="text-primary-foreground/80 text-sm">{store.city}, {store.state}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="text-sm font-semibold">{store.rating.toFixed(1)}</span>
                  <span className="text-primary-foreground/60 text-xs">({store.totalOrders} orders)</span>
                </div>
              </div>
            </div>
            <Badge className="bg-primary-foreground/20 text-primary-foreground border-0 self-start sm:self-auto">
              {store.isOpen ? "Open now" : "Closed"} • {store.statusText}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Store className="h-5 w-5 text-primary" /> Business Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ImageUpload label="Store logo" variant="logo" layout="stacked" value={form.logoUrl} onChange={(url) => updateField("logoUrl", url)} folder={UploadFolder.Stores} />
            <ImageUpload label="Store banner" variant="banner" layout="stacked" value={form.bannerUrl} onChange={(url) => updateField("bannerUrl", url)} folder={UploadFolder.Stores} />
            <div className="space-y-2"><Label>Store Name</Label><Input value={form.storeName} onChange={(e) => updateField("storeName", e.target.value)} /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => updateField("description", e.target.value)} /></div>
            <div className="space-y-2"><Label>Referral Code</Label><Input value={store.referralCode} readOnly className="bg-muted" /></div>
            <div className="space-y-2"><Label>KYC Verified</Label><Input value={store.kycVerified ? "Yes" : "Pending"} readOnly className="bg-muted" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> Contact & Location</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => updateField("email", e.target.value)} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} /></div>
            <div className="space-y-2"><Label>Address</Label><Input value={form.fullAddress} onChange={(e) => updateField("fullAddress", e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-2">
              <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => updateField("city", e.target.value)} /></div>
              <div className="space-y-2"><Label>State</Label><Input value={form.state} onChange={(e) => updateField("state", e.target.value)} /></div>
              <div className="space-y-2"><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => updateField("pincode", e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Delivery Radius (km)</Label><Input value={form.deliveryRadius} onChange={(e) => updateField("deliveryRadius", e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Delivery & Hours</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center justify-between sm:gap-4">
                <Label>Self Delivery</Label>
                <Switch checked={form.hasSelfDelivery} onCheckedChange={(v) => updateField("hasSelfDelivery", v)} />
              </div>
              <Separator orientation="vertical" className="h-8 hidden sm:block" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                <div className="space-y-2"><Label>Min Order (₹)</Label><Input value={form.minOrderAmount} onChange={(e) => updateField("minOrderAmount", e.target.value)} /></div>
                <div className="space-y-2"><Label>Delivery Charge (₹)</Label><Input value={form.deliveryCharge} onChange={(e) => updateField("deliveryCharge", e.target.value)} /></div>
                <div className="space-y-2"><Label>Free Above (₹)</Label><Input value={form.freeDeliveryAbove} onChange={(e) => updateField("freeDeliveryAbove", e.target.value)} /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="space-y-2"><Label>Opening Time</Label><Input type="time" value={form.openingTime} onChange={(e) => updateField("openingTime", e.target.value)} /></div>
              <div className="space-y-2"><Label>Closing Time</Label><Input type="time" value={form.closingTime} onChange={(e) => updateField("closingTime", e.target.value)} /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-stretch sm:justify-end">
        <Button onClick={handleSave} disabled={updateStore.isPending} className="w-full gap-2 sm:w-auto">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default StoreProfilePage;
